import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
  DragIndicator as DragIndicatorIcon,
  InfoOutlined as InfoOutlinedIcon,
  Refresh as RefreshIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import { type SxProps, type Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../api';
import { parseStatementXmlSegments, serializeStatementXmlSegments } from './parse';
import { createEmptySentenceRow, parseRowEditedFlag, segmentsToSentenceRows, sentenceRowsToPlainText, sentenceRowsToSegments } from './rows';
import type { StatementSegment, StatementSentenceRow } from './types';

export type StatementXmlEditorProps = {
  segments: StatementSegment[];
  onSegmentsChange: (segments: StatementSegment[]) => void;
  language: string;
  sx?: SxProps<Theme>;
  disabled?: boolean;
  showBottomAdd?: boolean;
  disableClpAssignment?: boolean;
  referenceSegments?: StatementSegment[] | null;
  referenceLabel?: string;
};

type ClpCodeSearchEntry = {
  group_id: number;
  variant: number;
  regulatory_code: string;
  text: string;
  text_base: string;
  has_variable: boolean;
  score: number;
  is_selected: boolean;
};

const DEBOUNCE_MS = 250;
const SEARCH_LIMIT = 20;

// Inputs sit borderless inside the table; the outline only appears on hover/focus.
const quietInputSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'transparent',
    transition: 'background-color 120ms ease',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'transparent',
    transition: 'border-color 120ms ease',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'divider',
  },
  '& .MuiOutlinedInput-root.Mui-focused': {
    bgcolor: 'background.paper',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'primary.main',
    borderWidth: 1,
  },
};

const reorderRows = (rows: StatementSentenceRow[], fromIndex: number, toIndex: number): StatementSentenceRow[] => {
  const next = [...rows];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

const withAssignedClp = (row: StatementSentenceRow, entry: ClpCodeSearchEntry | null): StatementSentenceRow => {
  if (!entry) {
    const nextAttributes = { ...row.attributes };
    delete nextAttributes.code;
    delete nextAttributes.groupId;
    delete nextAttributes.hasVariable;
    delete nextAttributes.variantId;
    nextAttributes.isEdited = 'true';

    return {
      ...row,
      sourceKind: 'node',
      nodeType: 'StatementNode',
      code: undefined,
      groupId: undefined,
      hasVariable: undefined,
      variantId: undefined,
      isEdited: true,
      attributes: nextAttributes,
    };
  }

  return {
    ...row,
    sourceKind: 'node',
    nodeType: 'GlossaryStatementNode',
    code: entry.regulatory_code,
    groupId: String(entry.group_id),
    hasVariable: entry.has_variable,
    variantId: String(entry.variant),
    isEdited: true,
    attributes: {
      ...row.attributes,
      code: entry.regulatory_code,
      groupId: String(entry.group_id),
      hasVariable: entry.has_variable ? 'true' : 'false',
      variantId: String(entry.variant),
      isEdited: 'true',
    },
  };
};

const parseGroupId = (raw: string | undefined): number | null => {
  if (!raw) return null;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
};

const parseVariantId = (raw: string | undefined): number => {
  if (!raw) return 0;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return 0;
  return Math.trunc(parsed);
};

const getRowSectionId = (row: StatementSentenceRow): string | null => {
  const rawSectionId =
    row.id ??
    row.attributes.id ??
    row.attributes.sectionId ??
    row.attributes.section_id ??
    row.attributes.sectionID;

  if (!rawSectionId) return null;
  const normalized = rawSectionId.trim();
  return normalized.length > 0 ? normalized : null;
};

export const StatementXmlEditor: React.FC<StatementXmlEditorProps> = ({
  segments,
  onSegmentsChange,
  language,
  sx,
  disabled = false,
  showBottomAdd = true,
  disableClpAssignment = false,
  referenceSegments = null,
  referenceLabel,
}) => {
  const api = useApi();
  const { t } = useTranslation();

  const [rows, setRows] = useState<StatementSentenceRow[]>([]);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [reanalyzeError, setReanalyzeError] = useState<string | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  const [clpQueryByRowId, setClpQueryByRowId] = useState<Record<string, string>>({});
  const [clpOptionsByRowId, setClpOptionsByRowId] = useState<Record<string, ClpCodeSearchEntry[]>>({});
  const [clpLoadingByRowId, setClpLoadingByRowId] = useState<Record<string, boolean>>({});
  const [restoreLoadingByRowId, setRestoreLoadingByRowId] = useState<Record<string, boolean>>({});

  const [draggingRowId, setDraggingRowId] = useState<string | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  const searchTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Edits made inside the editor are echoed back through the `segments` prop by
  // every consumer. Rebuilding rows from that echo regenerates row ids, which
  // loses focus, drops per-row autocomplete state and trims text the user is
  // still typing. Track what we emitted and only rebuild on real external changes.
  const lastEmittedXmlRef = useRef<string | null>(null);

  // Async handlers (debounced search, restore) must read the latest rows, not
  // the ones captured when they were scheduled.
  const rowsRef = useRef<StatementSentenceRow[]>(rows);
  rowsRef.current = rows;

  const clearPerRowState = () => {
    searchTimersRef.current.forEach((timerId) => clearTimeout(timerId));
    searchTimersRef.current.clear();
    setClpQueryByRowId({});
    setClpOptionsByRowId({});
    setClpLoadingByRowId({});
    setRestoreLoadingByRowId({});
  };

  useEffect(() => {
    const incomingXml = serializeStatementXmlSegments(segments);
    if (lastEmittedXmlRef.current !== null && incomingXml === lastEmittedXmlRef.current) return;
    lastEmittedXmlRef.current = null;
    setRows(segmentsToSentenceRows(segments));
    clearPerRowState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments]);

  useEffect(() => {
    return () => {
      searchTimersRef.current.forEach((timerId) => clearTimeout(timerId));
      searchTimersRef.current.clear();
    };
  }, []);

  const emitRows = (nextRows: StatementSentenceRow[]) => {
    setRows(nextRows);
    const nextSegments = sentenceRowsToSegments(nextRows);
    lastEmittedXmlRef.current = serializeStatementXmlSegments(nextSegments);
    onSegmentsChange(nextSegments);
  };

  const plainText = useMemo(() => sentenceRowsToPlainText(rows), [rows]);
  const referenceTextBySectionId = useMemo(() => {
    const bySectionId: Record<string, string> = {};
    if (!referenceSegments) return bySectionId;

    segmentsToSentenceRows(referenceSegments).forEach((row) => {
      const sectionId = getRowSectionId(row);
      if (!sectionId || bySectionId[sectionId]) return;

      const text = row.text.trim();
      if (!text) return;
      bySectionId[sectionId] = text;
    });

    return bySectionId;
  }, [referenceSegments]);

  const setRowLoading = (rowId: string, loading: boolean) => {
    setClpLoadingByRowId((prev) => ({ ...prev, [rowId]: loading }));
  };

  const performSearch = async (rowId: string, rawQuery: string, forceOpen: boolean) => {
    const row = rowsRef.current.find((candidate) => candidate.rowId === rowId);
    if (!row || disabled || disableClpAssignment) return;

    const normalizedLanguage = (language || '').trim().toLowerCase();
    if (!normalizedLanguage) return;

    const query = rawQuery.trim();
    const sentence = row.text.trim();
    if (!forceOpen && query.length < 2 && sentence.length === 0) {
      setClpOptionsByRowId((prev) => ({ ...prev, [rowId]: [] }));
      return;
    }

    setRowLoading(rowId, true);
    try {
      const params = new URLSearchParams();
      params.set('language', normalizedLanguage);
      params.set('limit', String(SEARCH_LIMIT));
      if (query) params.set('q', query);
      if (sentence) params.set('sentence', sentence.slice(0, 300));

      const selectedGroupId = parseGroupId(row.groupId);
      const selectedVariant = parseVariantId(row.variantId);
      if (selectedGroupId !== null) {
        params.set('selected_group_id', String(selectedGroupId));
        params.set('selected_variant', String(selectedVariant));
      }

      const response = await api.get(`/translations/clp-codes/search?${params.toString()}`);
      const options = Array.isArray(response) ? (response as ClpCodeSearchEntry[]) : [];
      setClpOptionsByRowId((prev) => ({ ...prev, [rowId]: options }));
    } catch {
      setClpOptionsByRowId((prev) => ({ ...prev, [rowId]: [] }));
    } finally {
      setRowLoading(rowId, false);
    }
  };

  const scheduleSearch = (rowId: string, rawQuery: string, forceOpen = false) => {
    const existingTimer = searchTimersRef.current.get(rowId);
    if (existingTimer) clearTimeout(existingTimer);

    const nextTimer = setTimeout(() => {
      void performSearch(rowId, rawQuery, forceOpen);
      searchTimersRef.current.delete(rowId);
    }, forceOpen ? 0 : DEBOUNCE_MS);

    searchTimersRef.current.set(rowId, nextTimer);
  };

  const handleAddAtEnd = () => {
    emitRows([...rows, createEmptySentenceRow()]);
  };

  const handleDelete = (rowId: string) => {
    emitRows(rows.filter((row) => row.rowId !== rowId));
    setClpQueryByRowId((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    setClpOptionsByRowId((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    setClpLoadingByRowId((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    setRestoreLoadingByRowId((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  };

  const handleTextChange = (rowId: string, text: string) => {
    const next = rows.map((row) => {
      if (row.rowId !== rowId) return row;
      return {
        ...row,
        text,
        isEdited: true,
        attributes: {
          ...row.attributes,
          isEdited: 'true',
        },
      };
    });
    emitRows(next);
  };

  const handleClpChange = (rowId: string, entry: ClpCodeSearchEntry | null) => {
    if (disableClpAssignment) return;

    const next = rows.map((row) => {
      if (row.rowId !== rowId) return row;
      const wasEmpty = row.text.trim().length === 0;
      const assigned = withAssignedClp(row, entry);

      if (!entry || !wasEmpty) return assigned;

      return {
        ...assigned,
        text: entry.text,
        isEdited: true,
        attributes: {
          ...assigned.attributes,
          isEdited: 'true',
        },
      };
    });
    emitRows(next);

    setClpQueryByRowId((prev) => ({
      ...prev,
      [rowId]: entry ? entry.regulatory_code : '',
    }));
    setRestoreError(null);
  };

  const setRestoreRowLoading = (rowId: string, loading: boolean) => {
    setRestoreLoadingByRowId((prev) => ({ ...prev, [rowId]: loading }));
  };

  const handleRestoreFromGlossary = async (rowId: string) => {
    if (disabled || !disableClpAssignment) return;

    const row = rowsRef.current.find((candidate) => candidate.rowId === rowId);
    if (!row) return;

    const normalizedLanguage = (language || '').trim().toLowerCase();
    const selectedGroupId = parseGroupId(row.groupId);
    const selectedVariant = parseVariantId(row.variantId);
    const rowCode = (row.code || '').trim();
    if (!normalizedLanguage || (selectedGroupId === null && !rowCode)) return;

    setRestoreRowLoading(rowId, true);
    setRestoreError(null);

    try {
      const params = new URLSearchParams();
      params.set('language', normalizedLanguage);
      params.set('limit', '10');
      if (selectedGroupId !== null) {
        params.set('selected_group_id', String(selectedGroupId));
        params.set('selected_variant', String(selectedVariant));
      }
      if (rowCode) params.set('q', rowCode);

      const response = await api.get(`/translations/clp-codes/search?${params.toString()}`);
      const options = Array.isArray(response) ? (response as ClpCodeSearchEntry[]) : [];

      const selectedOption =
        options.find(
          (entry) =>
            selectedGroupId !== null &&
            entry.group_id === selectedGroupId &&
            entry.variant === selectedVariant
        ) ??
        options.find((entry) => !!rowCode && entry.regulatory_code.toLowerCase() === rowCode.toLowerCase()) ??
        options[0];

      const glossaryText = selectedOption?.text?.trim();
      if (!glossaryText) {
        setRestoreError(t('statementXml.restoreFromGlossaryFailed'));
        return;
      }

      const next = rowsRef.current.map((candidate) => {
        if (candidate.rowId !== rowId) return candidate;
        return {
          ...candidate,
          text: glossaryText,
          isEdited: true,
          attributes: {
            ...candidate.attributes,
            isEdited: 'true',
          },
        };
      });
      emitRows(next);
    } catch {
      setRestoreError(t('statementXml.restoreFromGlossaryFailed'));
    } finally {
      setRestoreRowLoading(rowId, false);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLTableRowElement>, rowIndex: number) => {
    if (!draggingRowId || disabled) return;
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    setDropIndex(event.clientY < midpoint ? rowIndex : rowIndex + 1);
  };

  const handleDrop = () => {
    if (!draggingRowId || dropIndex === null || disabled) {
      setDraggingRowId(null);
      setDropIndex(null);
      return;
    }

    const fromIndex = rows.findIndex((row) => row.rowId === draggingRowId);
    if (fromIndex < 0) {
      setDraggingRowId(null);
      setDropIndex(null);
      return;
    }

    let targetIndex = dropIndex;
    if (targetIndex > fromIndex) targetIndex -= 1;
    if (targetIndex < 0) targetIndex = 0;
    if (targetIndex >= rows.length) targetIndex = rows.length - 1;

    emitRows(reorderRows(rows, fromIndex, targetIndex));
    setDraggingRowId(null);
    setDropIndex(null);
  };

  const handleReanalyze = async () => {
    if (disabled || reanalyzing || !language || !plainText.trim()) return;

    setReanalyzing(true);
    setReanalyzeError(null);

    try {
      const response = await api.post('/convert-to-xml', { text: plainText, language });
      const convertedText = (response && (response.converted_text || response.convertedText)) as string | undefined;
      if (!convertedText) {
        setReanalyzeError(t('statementXml.reanalyzeFailed'));
        return;
      }

      clearPerRowState();
      emitRows(segmentsToSentenceRows(parseStatementXmlSegments(convertedText)));
    } catch {
      setReanalyzeError(t('statementXml.reanalyzeFailed'));
    } finally {
      setReanalyzing(false);
    }
  };

  return (
    <Box sx={sx}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {t('translation.editInstruction')}
        </Typography>
        <Button
          variant="text"
          size="small"
          startIcon={reanalyzing ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={handleReanalyze}
          disabled={disabled || reanalyzing || !plainText.trim()}
          sx={{ minHeight: 30, px: 1, color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'transparent' } }}
        >
          {t('statementXml.reanalyzeAction')}
        </Button>
      </Stack>

      <TableContainer
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <Table
          size="small"
          sx={{
            tableLayout: 'fixed',
            '& th, & td': {
              verticalAlign: 'top',
            },
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  bgcolor: 'background.default',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  py: 0.75,
                  fontSize: '0.68rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'text.secondary',
                },
              }}
            >
              <TableCell sx={{ width: 64 }}>#</TableCell>
              <TableCell sx={{ width: disableClpAssignment ? 150 : 240 }}>{t('statementXml.tableClpTag')}</TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <span>{t('statementXml.tableSentence')}</span>
                  {Object.keys(referenceTextBySectionId).length > 0 && (
                    <Tooltip title={referenceLabel || t('statementXml.referenceSentenceLabel')}>
                      <InfoOutlinedIcon
                        fontSize="inherit"
                        sx={{ cursor: 'help', fontSize: '0.9rem', color: 'text.secondary' }}
                      />
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
              <TableCell sx={{ width: disableClpAssignment ? 96 : 72 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} sx={{ py: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('statementXml.tableEmpty')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {rows.map((row, index) => {
              const rowOptions = clpOptionsByRowId[row.rowId] ?? [];
              const selectedGroupId = parseGroupId(row.groupId);
              const selectedVariant = parseVariantId(row.variantId);
              const activeCode =
                rowOptions.find(
                  (entry) =>
                    selectedGroupId !== null &&
                    entry.group_id === selectedGroupId &&
                    entry.variant === selectedVariant
                ) ??
                (row.code
                  ? {
                      group_id: selectedGroupId ?? -1,
                      variant: selectedVariant,
                      regulatory_code: row.code,
                      text: row.text,
                      text_base: '',
                      has_variable: !!row.hasVariable,
                      score: 0,
                      is_selected: true,
                    }
                  : null);

              const query = clpQueryByRowId[row.rowId] ?? '';
              const isDropBefore = dropIndex === index;
              const isDropAfterLast = dropIndex === rows.length && index === rows.length - 1;
              const noOptionsText = query.trim().length < 2 && row.text.trim().length === 0
                ? t('statementXml.clpTypeMoreChars')
                : t('statementXml.clpOptionNoResults');
              const sectionId = getRowSectionId(row);
              const referenceText = sectionId ? (referenceTextBySectionId[sectionId] ?? '') : '';
              const isUserEdited = parseRowEditedFlag(row);
              const canRestoreFromGlossary =
                disableClpAssignment && (((row.groupId || '').trim().length > 0) || ((row.code || '').trim().length > 0));
              const restoreLoading = !!restoreLoadingByRowId[row.rowId];

              return (
                <TableRow
                  key={row.rowId}
                  draggable={!disabled}
                  onDragStart={() => !disabled && setDraggingRowId(row.rowId)}
                  onDragOver={(event) => handleDragOver(event, index)}
                  onDrop={handleDrop}
                  onDragEnd={() => {
                    setDraggingRowId(null);
                    setDropIndex(null);
                  }}
                  sx={(theme) => ({
                    '& td': {
                      borderBottom: index === rows.length - 1 ? 'none' : `1px solid ${theme.palette.divider}`,
                      borderTop: isDropBefore ? `2px solid ${theme.palette.primary.main}` : undefined,
                      ...(isDropAfterLast ? { borderBottom: `2px solid ${theme.palette.primary.main}` } : {}),
                      py: 0.75,
                    },
                    opacity: draggingRowId === row.rowId ? 0.55 : 1,
                    transition: 'background-color 120ms ease',
                    '&:hover': { bgcolor: 'action.hover' },
                    '&:hover .row-drag-handle': { color: 'text.secondary' },
                    '&:hover .row-actions': { opacity: 1 },
                  })}
                >
                  <TableCell>
                    <Stack direction="row" spacing={0.25} alignItems="center" sx={{ pt: 0.9 }}>
                      <Tooltip title={t('statementXml.dragToReorder')}>
                        <Box
                          className="row-drag-handle"
                          sx={{
                            display: 'inline-flex',
                            color: 'text.disabled',
                            cursor: disabled ? 'default' : 'grab',
                            transition: 'color 120ms ease',
                          }}
                        >
                          <DragIndicatorIcon sx={{ fontSize: 18 }} />
                        </Box>
                      </Tooltip>
                      <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {index + 1}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {disableClpAssignment ? (
                      <Box sx={{ pt: 0.6 }}>
                        {row.code ? (
                          <Chip
                            size="small"
                            label={row.code}
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.72rem',
                              bgcolor: 'action.hover',
                              color: 'text.primary',
                              borderRadius: 1,
                            }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled" sx={{ px: 0.5 }}>
                            {t('statementXml.unassigned')}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Autocomplete
                        size="small"
                        options={rowOptions}
                        loading={!!clpLoadingByRowId[row.rowId]}
                        value={activeCode}
                        inputValue={query}
                        onOpen={() => scheduleSearch(row.rowId, query, true)}
                        onInputChange={(_, nextValue, reason) => {
                          setClpQueryByRowId((prev) => ({ ...prev, [row.rowId]: nextValue }));
                          if (reason === 'input') {
                            scheduleSearch(row.rowId, nextValue, false);
                          }
                        }}
                        onChange={(_, next) => handleClpChange(row.rowId, next)}
                        getOptionLabel={(option) => `${option.regulatory_code} - ${option.text}`}
                        isOptionEqualToValue={(option, value) =>
                          option.group_id === value.group_id && option.variant === value.variant
                        }
                        disabled={disabled}
                        noOptionsText={noOptionsText}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={t('statementXml.clpAutocompletePlaceholder')}
                            sx={quietInputSx}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box
                            component="li"
                            {...props}
                            title={`${option.regulatory_code} - ${option.text}`}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              textAlign: 'left',
                              width: '100%',
                              py: 1,
                            }}
                          >
                            <Typography variant="body2" fontWeight={700} sx={{ width: '100%', textAlign: 'left' }}>
                              {option.regulatory_code}
                            </Typography>
                            <Tooltip title={option.text} placement="right" arrow>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  width: '100%',
                                  textAlign: 'left',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {option.text}
                              </Typography>
                            </Tooltip>
                          </Box>
                        )}
                        slotProps={{
                          popper: {
                            sx: {
                              '& .MuiAutocomplete-paper': {
                                overflow: 'hidden',
                              },
                            },
                          },
                          listbox: {
                            sx: {
                              maxHeight: 320,
                              overflowY: 'auto',
                            },
                          },
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <TextField
                        fullWidth
                        size="small"
                        value={row.text}
                        onChange={(event) => handleTextChange(row.rowId, event.target.value)}
                        placeholder={t('statementXml.sentencePlaceholder')}
                        disabled={disabled}
                        multiline
                        minRows={1}
                        maxRows={4}
                        sx={quietInputSx}
                      />
                      {referenceText.length > 0 && (
                        <Tooltip title={referenceText} placement="top-start" arrow>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
                            <Box
                              component="span"
                              sx={{
                                mt: 0.5,
                                ml: 1.75,
                                pl: 0.9,
                                pr: 0.25,
                                borderLeft: '2px solid',
                                borderColor: 'divider',
                                display: 'block',
                                whiteSpace: 'normal',
                                overflowWrap: 'anywhere',
                                wordBreak: 'break-word',
                                opacity: 0.9,
                              }}
                            >
                              {referenceText}
                            </Box>
                          </Typography>
                        </Tooltip>
                      )}
                      {isUserEdited && (
                        <Tooltip title={t('statementXml.userEditedHint')} placement="top-start" arrow>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t('statementXml.userEdited')}
                            sx={{
                              mt: 0.5,
                              ml: 1.75,
                              height: 18,
                              fontSize: '0.65rem',
                              color: 'text.secondary',
                              borderColor: 'divider',
                              opacity: 0.85,
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Stack
                      direction="row"
                      spacing={0}
                      className="row-actions"
                      sx={{ pt: 0.35, opacity: { xs: 1, md: 0.35 }, transition: 'opacity 120ms ease' }}
                    >
                      {disableClpAssignment && (
                        <Tooltip
                          title={
                            canRestoreFromGlossary
                              ? t('statementXml.restoreFromGlossary')
                              : t('statementXml.restoreFromGlossaryDisabled')
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => void handleRestoreFromGlossary(row.rowId)}
                              disabled={disabled || !canRestoreFromGlossary || restoreLoading}
                            >
                              {restoreLoading ? <CircularProgress size={14} /> : <RestoreIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title={t('statementXml.deleteSentence')}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row.rowId)}
                            disabled={disabled}
                            sx={{ '&:hover': { color: 'error.main' } }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {showBottomAdd && (
          <Button
            fullWidth
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={handleAddAtEnd}
            disabled={disabled}
            sx={{
              borderRadius: 0,
              borderTop: '1px solid',
              borderColor: 'divider',
              py: 0.9,
              minHeight: 0,
              color: 'text.secondary',
              fontSize: '0.8rem',
              justifyContent: 'center',
              '&:hover': { bgcolor: 'action.hover', color: 'primary.main' },
            }}
          >
            {t('statementXml.addSentenceBottom')}
          </Button>
        )}
      </TableContainer>

      {reanalyzeError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {reanalyzeError}
        </Alert>
      )}
      {restoreError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {restoreError}
        </Alert>
      )}
    </Box>
  );
};
